import React, { useContext, useEffect, useState } from 'react'
import classnames from 'classnames'
import deepEqual from 'react-fast-compare'
import { NotificationListContext } from './notification-list-context'
import { Button, Input, Popover, Select as AntDSelect, Skeleton, Switch, Tooltip } from 'antd'
import { REFRESH_OPTIONS } from './constants'
import { BetterSelect } from '../better-select/better-select'
import { NotificationSource } from '../../enums/notification-source'
import { HOLD_OUT_COMPUTED_STATUS, StatusType } from '../../enums/status-type'
import { CaretDownOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons'
import { NotificationListState } from './interfaces'
import { AppService, DomainService } from '../../services'
import { Container } from 'typescript-ioc/es5'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import ShowSingleDomainEntitiesToggle from '../show-single-domain-entities-toggle/show-single-domain-entities-toggle'
import { NotificationListTypes } from './enums'
import { SegmentDto } from '../../dtos/segment'
import { Select } from '@pushly/aqe/lib/components'
import { SegmentSelect } from '../segment-select/segment-select'
import { useService } from '@pushly/aqe/lib/hooks'

type FilterKey = keyof NotificationListState['filters']

interface INotificationListHeaderProps {
    title?: string
    filterSize?: SizeType
    filtersAddonBefore?: React.ReactNode
    filtersAddonAfter?: React.ReactNode
    hideRefresh?: boolean
    hideAutoRefreshOptions?: boolean
    hideFilters?: FilterKey | FilterKey[] | boolean
    filtersDisabled?: FilterKey | FilterKey[] | boolean
    listType?: NotificationListTypes
    segments?: SegmentDto[]
}

interface IState {
    status?: string[]
    source?: string[]
    segments?: { value: number; label: string }[]
}

const NotificationListHeader = (props: INotificationListHeaderProps) => {
    const {
        initialLoadComplete,
        levelDependenciesLoaded,
        dataSourceLoaded,
        refreshing,
        filters,
        onSearchClick,
        onFilterChange,
        onRefreshClick,
        refreshEnabled,
        refreshTimeout,
        onRefreshEnabledChange,
        onRefreshTimeoutChange,
        ...ctx
    } = useContext(NotificationListContext)

    const levelResourceId = ctx.level === 'domain' ? ctx.domain?.id : ctx.org?.id
    const [prevLevelResourceId, setPrevLevelResourceId] = useState(levelResourceId)

    let liveTypeTimeout: any = null

    const [status, setStatus] = useState<string[]>([])
    const [source, setSource] = useState<string[]>([])
    const [segments, setSegments] = useState<{ value: number; label: string }[]>([])

    useEffect(() => {
        if (prevLevelResourceId !== levelResourceId) {
            if (filters.status && !deepEqual(status, filters.status)) {
                setStatus(filters.status)
            }

            if (filters.source && !deepEqual(status, filters.source)) {
                setSource(filters.source)
            }

            if (filters.segments && !deepEqual(status, filters.segments)) {
                setSegments(filters.segments)
            }

            setPrevLevelResourceId(levelResourceId)
        }
    }, [levelResourceId, prevLevelResourceId])

    const allFilters: FilterKey[] = ['source', 'status', 'search', 'showAllNotifications']
    const filterSize = props.filterSize ?? 'middle'
    const { listType } = props

    let filtersHidden: FilterKey[] = []
    if (props.hideFilters === true) {
        filtersHidden = Array.from(allFilters)
    } else if (props.hideFilters) {
        filtersHidden = Array.isArray(props.hideFilters) ? props.hideFilters : [props.hideFilters]
    }

    let filtersDisabled: FilterKey[] = []
    if (props.filtersDisabled === true) {
        filtersDisabled = Array.from(allFilters)
    } else if (props.filtersDisabled) {
        filtersDisabled = Array.isArray(props.filtersDisabled) ? props.filtersDisabled : [props.filtersDisabled]
    }

    const hideAllFilters = filtersHidden.length === allFilters.length
    const hideAllRefresh = props.hideRefresh && props.hideAutoRefreshOptions

    const loading = !initialLoadComplete && !levelDependenciesLoaded && !dataSourceLoaded
    const statusOptions = [
        { value: StatusType.COMPLETED.name, label: 'Delivered' },
        { value: StatusType.SCHEDULED.name, label: 'Scheduled' },
        { value: StatusType.DELIVERING.name, label: 'Delivering' },
        { value: HOLD_OUT_COMPUTED_STATUS, label: 'Hold Out' },
        { value: StatusType.CANCELLED.name, label: 'Cancelled' },
        { value: StatusType.FAILED.name, label: 'Failed' },
    ]

    return (
        <div className={classnames('notification-list-header')}>
            <div
                className={classnames('notification-list-title', {
                    hidden: !props.title,
                })}
            >
                <span>{props.title}</span>
            </div>

            <div
                className={classnames('notification-list-filters', {
                    hidden: hideAllFilters,
                })}
            >
                {props.filtersAddonBefore}

                {!filtersHidden.includes('search') && (
                    <Input.Search
                        className={classnames('notification-list-search')}
                        size={filterSize}
                        placeholder="Search by ID or Notification Content"
                        disabled={filtersDisabled.includes('search')}
                        enterButton={true}
                        defaultValue={filters.search}
                        onSearch={onSearchClick}
                        onChange={(ev) => {
                            clearTimeout(liveTypeTimeout)

                            const value = ev.target.value
                            liveTypeTimeout = setTimeout(() => {
                                onFilterChange('search', value)
                            }, 320)
                        }}
                    />
                )}

                {!filtersHidden.includes('status') && listType === NotificationListTypes.NOTIFICATION && (
                    <Select
                        className="status-filter-select"
                        disabled={loading || filtersDisabled.includes('status')}
                        size={filterSize === 'middle' ? 'middle' : filterSize}
                        mode="multiple"
                        prefix="Status"
                        prefixWidth={50}
                        placeholder="All Statuses"
                        showArrow={true}
                        suffixIcon={<CaretDownOutlined />}
                        defaultValue={filters.status}
                        options={statusOptions}
                        value={status}
                        onChange={(value: string[]) => {
                            setStatus(value)
                        }}
                        onDropdownVisibleChange={(open: boolean) => {
                            if (!open) {
                                onFilterChange('status', status)
                            }
                        }}
                        showSelectAll={true}
                        showSearch={false}
                        selectAllLabel="All Statuses"
                        selectAllSuffixIconScopeEnabled={<CheckOutlined />}
                        tagStyle="ghost"
                        maxTagCount={'responsive'}
                        maxTagPlaceholder={(omittedValues) => {
                            if (omittedValues.length === statusOptions.length) {
                                return 'All Statuses'
                            }
                            return (
                                <>
                                    <span>
                                        {omittedValues.length} {omittedValues.length > 1 ? 'Statuses' : 'Status'}
                                    </span>
                                </>
                            )
                        }}
                    />
                )}

                {!filtersHidden.includes('source') && listType === NotificationListTypes.NOTIFICATION && (
                    <Select
                        className="source-filter-select"
                        disabled={loading || filtersDisabled.includes('source')}
                        size={filterSize === 'middle' ? 'middle' : filterSize}
                        mode="multiple"
                        prefix="Source"
                        prefixWidth={54}
                        placeholder="All Sources"
                        showArrow={true}
                        suffixIcon={<CaretDownOutlined />}
                        defaultValue={filters.source}
                        value={source}
                        options={[
                            { value: NotificationSource.MANUAL, label: 'Manual' },
                            { value: NotificationSource.FEED, label: 'Feed' },
                        ]}
                        onChange={(value: string[]) => {
                            setSource(value)
                        }}
                        onDropdownVisibleChange={(open: boolean) => {
                            if (!open) {
                                onFilterChange('source', source)
                            }
                        }}
                        showSelectAll={false}
                        showSearch={false}
                        tagStyle="ghost"
                        maxTagCount={2}
                        maxTagPlaceholder={(omittedValues) => (
                            <Popover content={omittedValues.map((o) => o.label).join(', ')}>
                                <span>
                                    {omittedValues.length} {omittedValues.length > 1 ? 'Sources' : 'Source'}
                                </span>
                            </Popover>
                        )}
                    />
                )}

                {!filtersHidden.includes('showAllNotifications') && (
                    <ShowSingleDomainEntitiesToggle
                        className="show-all-notifications-filter"
                        title="Notifications"
                        defaultChecked={filters.showAllNotifications}
                        onChange={(enabled) => {
                            onFilterChange('showAllNotifications', enabled)
                        }}
                    />
                )}

                {!filtersHidden.includes('segments') && (
                    // (loading ? (
                    //     <Skeleton.Input
                    //         active={true}
                    //         size={'default'}
                    //         style={{ width: 200, height: 32, lineHeight: 'normal' }}
                    //     />
                    // ) : (
                    <SegmentSelect
                        loading={loading}
                        className={(filters.segments?.length ?? 0) === 0 ? ' implicit-all-segments-selected' : ''}
                        mode="multiple"
                        showSelectAll={true}
                        selectAllSuffixIconScopeEnabled={<CheckOutlined />}
                        allowClear={true}
                        placeholder="All Segments"
                        prefixWidth={73}
                        tagStyle="ghost"
                        segments={props.segments ?? []}
                        defaultValue={filters.segments ?? []}
                        labelInValue={true}
                        suffixIcon={<CaretDownOutlined />}
                        showArrow={true}
                        singleLine={true}
                        dropdownMatchSelectWidth={425}
                        maxTagCount={(filters.segments ?? []).length > 1 ? 0 : 1}
                        maxTagPlaceholder={(omittedValues) => (
                            <Popover
                                title="Segments"
                                content={
                                    <ul>
                                        {omittedValues.map((o) => (
                                            <li key={o.value}>{o.label}</li>
                                        ))}
                                    </ul>
                                }
                                overlayClassName="segment-filter-popover"
                            >
                                <span>
                                    {omittedValues.length} {omittedValues.length > 1 ? 'Segments' : 'Segment'}
                                </span>
                            </Popover>
                        )}
                        onChange={(value, option) => {
                            setSegments(value)
                        }}
                        onDropdownVisibleChange={(open?: boolean) => {
                            if (!open) {
                                onFilterChange('segments', segments)
                            }
                        }}
                        onClear={() => {
                            setSegments([])
                            onFilterChange('segments', [])
                        }}
                    />
                )}

                {props.filtersAddonAfter}
            </div>

            <div
                className={classnames('notification-list-refresh', {
                    hidden: hideAllRefresh,
                })}
            >
                {!props.hideAutoRefreshOptions && (
                    <>
                        <Switch size="small" checked={refreshEnabled} onChange={onRefreshEnabledChange} />
                        <span>Auto-Refresh</span>

                        <Select
                            className={classnames('notification-list-timeout-select')}
                            size="small"
                            disabled={!levelDependenciesLoaded || !refreshEnabled}
                            options={REFRESH_OPTIONS}
                            value={refreshTimeout}
                            onChange={onRefreshTimeoutChange}
                        />
                    </>
                )}

                {!props.hideRefresh && (
                    <Tooltip title="Refresh">
                        <Button
                            className={classnames('notification-list-refresh-toggle')}
                            size="small"
                            shape="round"
                            onClick={onRefreshClick}
                            disabled={!levelDependenciesLoaded || !refreshEnabled}
                        >
                            <ReloadOutlined spin={loading || refreshing} />
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    )
}

export default NotificationListHeader
